import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Select, Space, Spin, Typography, Button, Drawer, Table, Divider, Tag, Grid } from 'antd';
import {
    DollarCircleOutlined,
    ShoppingCartOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    FileExcelOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { orderService } from '../../services/orderService';
import * as XLSX from 'xlsx';

const { Option } = Select;
const { Text } = Typography;

const OrderSummary = ({ onFilterChange }) => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({ 
        counts: { Total: 0, Open: 0, InProgress: 0, Completed: 0, Closed: 0 }, 
        revenue: 0, 
        orders: [] 
    });
    const [period, setPeriod] = useState('all');
    const [value, setValue] = useState(undefined);
    const [isDetailVisible, setIsDetailVisible] = useState(false);

    // Determine default start year for current FY (Apr-Mar cycle)
    const now = new Date();
    const currentFYStartYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    const [year, setYear] = useState(currentFYStartYear);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const calculateDateRange = () => {
        if (period === 'all') return { startDate: null, endDate: null };

        let startDate = new Date(year, 3, 1); // April
        let endDate = new Date(year + 1, 2, 31, 23, 59, 59, 999); // March next year

        if (period === 'month' && value !== undefined) {
            const month = parseInt(value);
            const actualYear = month < 3 ? year + 1 : year;
            startDate = new Date(actualYear, month, 1);
            endDate = new Date(actualYear, month + 1, 0, 23, 59, 59, 999);
        } else if (period === 'quarter' && value !== undefined) {
            const quarter = parseInt(value);
            if (quarter === 1) {
                startDate = new Date(year, 3, 1);
                endDate = new Date(year, 5, 30, 23, 59, 59, 999);
            } else if (quarter === 2) {
                startDate = new Date(year, 6, 1);
                endDate = new Date(year, 8, 30, 23, 59, 59, 999);
            } else if (quarter === 3) {
                startDate = new Date(year, 9, 1);
                endDate = new Date(year, 11, 31, 23, 59, 59, 999);
            } else if (quarter === 4) {
                startDate = new Date(year + 1, 0, 1);
                endDate = new Date(year + 1, 2, 31, 23, 59, 59, 999);
            }
        }
        return { 
            startDate: startDate.toISOString().split('T')[0], 
            endDate: endDate.toISOString().split('T')[0] 
        };
    };

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await orderService.getOrderStats({ period, value, year });
            if (response.success) {
                setStats(response.data);
                
                if (onFilterChange) {
                    const range = calculateDateRange();
                    onFilterChange(range);
                }
            }
        } catch (error) {
            console.error('Failed to fetch order stats', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, [period, value, year]);

    const handlePeriodChange = (val) => {
        setPeriod(val);
        setValue(undefined);
    };

    const handleExport = () => {
        if (!stats.orders || stats.orders.length === 0) return;

        // Prepare data for XLSX
        const data = stats.orders.map(order => ({
            'Order ID': order.id,
            'Customer': order.customerName,
            'Date': new Date(order.orderDate).toLocaleDateString(),
            'Plant Size (kW)': order.plantSizeKw,
            'Status': order.projectStatus,
            'Agent': order.salesAgent,
            'Total Value': order.orderValue
        }));

        // Add summary statistics Row
        const summary = [
            {}, // Empty row
            {
                'Order ID': 'SUMMARY',
                'Customer': `Total Orders: ${stats.counts.Total}`,
                'Date': '',
                'Plant Size (kW)': '',
                'Status': '',
                'Agent': '',
                'Total Value': `Grand Total: ₹${stats.revenue.toLocaleString()}`
            }
        ];

        const worksheet = XLSX.utils.json_to_sheet([...data, ...summary]);

        // Auto-size columns
        const colWidths = [
            { wch: 10 },
            { wch: 30 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 20 },
            { wch: 20 }
        ];
        worksheet['!cols'] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Detailed Orders");

        XLSX.writeFile(workbook, `orders_${year}_${period}_${value || 'all'}.xlsx`);
    };

    const months = [
        { label: 'April', value: 3 },
        { label: 'May', value: 4 },
        { label: 'June', value: 5 },
        { label: 'July', value: 6 },
        { label: 'August', value: 7 },
        { label: 'September', value: 8 },
        { label: 'October', value: 9 },
        { label: 'November', value: 10 },
        { label: 'December', value: 11 },
        { label: 'January', value: 0 },
        { label: 'February', value: 1 },
        { label: 'March', value: 2 }
    ];

    const quarters = [
        { label: 'Q1 (Apr-Jun)', value: 1 },
        { label: 'Q2 (Jul-Sep)', value: 2 },
        { label: 'Q3 (Oct-Dec)', value: 3 },
        { label: 'Q4 (Jan-Mar)', value: 4 }
    ];

    const years = [2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030];

    const columns = [
        { 
            title: 'Order ID', 
            dataIndex: 'id', 
            key: 'id', 
            render: (id) => `#${id}`,
            width: isMobile ? 80 : 100
        },
        { title: 'Customer', dataIndex: 'customerName', key: 'customerName' },
        ...(!isMobile ? [
            {
                title: 'Date',
                dataIndex: 'orderDate',
                key: 'orderDate',
                render: (date) => new Date(date).toLocaleDateString()
            },
            {
                title: 'Size (kW)',
                dataIndex: 'plantSizeKw',
                key: 'plantSizeKw',
                render: (val) => `${val || 0} kW`
            },
            {
                title: 'Status',
                dataIndex: 'projectStatus',
                key: 'projectStatus',
                render: (status) => {
                    const colors = { Open: 'blue', InProgress: 'orange', Completed: 'green', Closed: 'default' };
                    return <Tag color={colors[status] || 'default'}>{status}</Tag>;
                }
            },
        ] : []),
        {
            title: 'Value',
            dataIndex: 'orderValue',
            key: 'orderValue',
            render: (val) => <Text strong>₹{(val || 0).toLocaleString()}</Text>
        },
    ];

    return (
        <div style={{ marginBottom: 24 }}>
            <Card bordered={false} styles={{ body: { padding: isMobile ? '12px' : '16px 24px' } }} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                        <Space size="middle">
                            <CalendarOutlined style={{ fontSize: isMobile ? 16 : 18, color: '#1890ff' }} />
                            {period !== 'all' ? (
                                <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>FY {year}-{String(year + 1).slice(-2)}</Text>
                            ) : (
                                <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>All Time Data</Text>
                            )}
                        </Space>
                        <Space wrap>
                            {period !== 'all' && (
                                <Select
                                    value={year}
                                    onChange={setYear}
                                    style={{ width: 100 }}
                                    placeholder="Year"
                                >
                                    {years.map(y => (
                                        <Option key={y} value={y}>{y}</Option>
                                    ))}
                                </Select>
                            )}

                            <Select
                                value={period}
                                onChange={handlePeriodChange}
                                style={{ width: isMobile ? 100 : 120 }}
                                placeholder="Select Period"
                            >
                                <Option value="all">All Time</Option>
                                <Option value="year">Full Year</Option>
                                <Option value="quarter">Quarterly</Option>
                                <Option value="month">Monthly</Option>
                            </Select>

                            {period === 'month' && (
                                <Select
                                    value={value}
                                    onChange={setValue}
                                    style={{ width: 150 }}
                                    placeholder="Select Month"
                                >
                                    {months.map(m => (
                                        <Option key={m.value} value={m.value}>{m.label}</Option>
                                    ))}
                                </Select>
                            )}

                            {period === 'quarter' && (
                                <Select
                                    value={value}
                                    onChange={setValue}
                                    style={{ width: 150 }}
                                    placeholder="Select Quarter"
                                >
                                    {quarters.map(q => (
                                        <Option key={q.value} value={q.value}>{q.label}</Option>
                                    ))}
                                </Select>
                            )}

                            <Divider type="vertical" />

                            <Button
                                type="primary"
                                ghost
                                icon={<InfoCircleOutlined />}
                                onClick={() => setIsDetailVisible(true)}
                                disabled={stats.counts.Total === 0}
                                size={isMobile ? 'middle' : 'default'}
                            >
                                {isMobile ? 'Details' : 'View Details'}
                            </Button>

                            <Button
                                icon={<FileExcelOutlined style={{ color: '#52c41a' }} />}
                                onClick={handleExport}
                                disabled={stats.counts.Total === 0}
                                size={isMobile ? 'middle' : 'default'}
                            >
                                {isMobile ? 'XLSX' : 'Export XLSX'}
                            </Button>
                        </Space>
                    </div>

                    <Spin spinning={loading}>
                        <Row gutter={[isMobile ? 12 : 20, isMobile ? 12 : 20]}>
                            <Col xs={12} sm={12} lg={6}>
                                <Card bordered={false} styles={{ body: { padding: isMobile ? '12px' : '24px' } }} style={{ borderRadius: 16, background: '#e6f7ff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden', height: '100%' }}>
                                    <div style={{ position: 'absolute', right: isMobile ? -5 : -10, top: isMobile ? -5 : -10, opacity: 0.1, fontSize: isMobile ? 40 : 60, color: '#1890ff' }}>
                                        <ShoppingCartOutlined />
                                    </div>
                                    <div style={{ position: 'relative', zIndex: 1 }}>
                                        <Text type="secondary" style={{ fontSize: isMobile ? 11 : 13, textTransform: 'uppercase', fontWeight: 600 }}>Total Orders</Text>
                                        <div style={{ marginTop: 4 }}><Text style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700 }}>{stats.counts.Total}</Text></div>
                                        <div style={{ marginTop: 4 }}>
                                            <Tag color="#1890ff" style={{ borderRadius: 10, border: 'none', fontSize: 9 }}>Live</Tag>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={12} lg={6}>
                                <Card bordered={false} styles={{ body: { padding: isMobile ? '12px' : '24px' } }} style={{ borderRadius: 16, background: '#fff7e6', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden', height: '100%' }}>
                                    <div style={{ position: 'absolute', right: isMobile ? -5 : -10, top: isMobile ? -5 : -10, opacity: 0.1, fontSize: isMobile ? 40 : 60, color: '#fa8c16' }}>
                                        <ClockCircleOutlined />
                                    </div>
                                    <div style={{ position: 'relative', zIndex: 1 }}>
                                        <Text type="secondary" style={{ fontSize: isMobile ? 11 : 13, textTransform: 'uppercase', fontWeight: 600 }}>In Progress</Text>
                                        <div style={{ marginTop: 4 }}><Text style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700 }}>{stats.counts.InProgress}</Text></div>
                                        <div style={{ marginTop: 4 }}>
                                            <Tag color="#fa8c16" style={{ borderRadius: 10, border: 'none', fontSize: 9 }}>Live</Tag>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={12} lg={6}>
                                <Card bordered={false} styles={{ body: { padding: isMobile ? '12px' : '24px' } }} style={{ borderRadius: 16, background: '#f6ffed', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden', height: '100%' }}>
                                    <div style={{ position: 'absolute', right: isMobile ? -5 : -10, top: isMobile ? -5 : -10, opacity: 0.1, fontSize: isMobile ? 40 : 60, color: '#52c41a' }}>
                                        <CheckCircleOutlined />
                                    </div>
                                    <div style={{ position: 'relative', zIndex: 1 }}>
                                        <Text type="secondary" style={{ fontSize: isMobile ? 11 : 13, textTransform: 'uppercase', fontWeight: 600 }}>Completed</Text>
                                        <div style={{ marginTop: 4 }}><Text style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700 }}>{stats.counts.Completed}</Text></div>
                                        <div style={{ marginTop: 4 }}>
                                            <Tag color="#52c41a" style={{ borderRadius: 10, border: 'none', fontSize: 9 }}>Live</Tag>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={12} lg={6}>
                                <Card bordered={false} styles={{ body: { padding: isMobile ? '12px' : '24px' } }} style={{ borderRadius: 16, background: '#fff0f6', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden', height: '100%' }}>
                                    <div style={{ position: 'absolute', right: isMobile ? -5 : -10, top: isMobile ? -5 : -10, opacity: 0.1, fontSize: isMobile ? 40 : 60, color: '#eb2f96' }}>
                                        <DollarCircleOutlined />
                                    </div>
                                    <div style={{ position: 'relative', zIndex: 1 }}>
                                        <Text type="secondary" style={{ fontSize: isMobile ? 11 : 13, textTransform: 'uppercase', fontWeight: 600 }}>Total Revenue</Text>
                                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'baseline', gap: 2 }}>
                                            <span style={{ fontSize: isMobile ? 14 : 18, color: '#eb2f96' }}>₹</span>
                                            <Text style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700 }}>{stats.revenue.toLocaleString(undefined, { minimumFractionDigits: 0 })}</Text>
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                            <Tag color="#eb2f96" style={{ borderRadius: 10, border: 'none', fontSize: 9 }}>Live</Tag>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </Spin>
                </Space>
            </Card>

            <Drawer
                title={isMobile ? `Orders - FY ${year}` : `Detailed Orders - FY ${year}-${String(year + 1).slice(-2)}`}
                placement="right"
                width={isMobile ? '100%' : 900}
                onClose={() => setIsDetailVisible(false)}
                open={isDetailVisible}
                extra={
                    <Button
                        type="primary"
                        ghost
                        icon={<FileExcelOutlined />}
                        onClick={handleExport}
                    >
                        Export XLSX
                    </Button>
                }
            >
                <div style={{ marginBottom: 24 }}>
                    <Row gutter={[12, 12]}>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ background: '#f0f5ff', borderLeft: '3px solid #1890ff', height: '100%' }}>
                                <Statistic title="Total" value={stats.counts.Total} valueStyle={{ fontSize: isMobile ? '16px' : '18px' }} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ background: '#fff7e6', borderLeft: '3px solid #fa8c16', height: '100%' }}>
                                <Statistic title="Pending" value={stats.counts.InProgress} valueStyle={{ fontSize: isMobile ? '16px' : '18px' }} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ background: '#f6ffed', borderLeft: '3px solid #52c41a', height: '100%' }}>
                                <Statistic title="Done" value={stats.counts.Completed} valueStyle={{ fontSize: isMobile ? '16px' : '18px' }} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ background: '#fff0f6', borderLeft: '3px solid #eb2f96', height: '100%' }}>
                                <Statistic title="Revenue" value={stats.revenue} prefix="₹" precision={0} valueStyle={{ fontSize: isMobile ? '14px' : '18px' }} />
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Table
                    dataSource={stats.orders}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 10, size: isMobile ? 'small' : 'default' }}
                    size={isMobile ? 'small' : 'middle'}
                />
            </Drawer>
        </div>
    );
};

export default OrderSummary;
